import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect, type ReactNode } from 'react';
import { FiUser, FiLogOut, FiHome, FiFileText, FiChevronDown, FiBell, FiTruck, FiShield, FiLock } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { notificacoesService } from '../../services/notificacoes.service';
import { useBranding } from '../../hooks/useBranding';
import { AcessibilidadeWidget } from '../AcessibilidadeWidget';
import { Button } from '../ui/Button';

export function Layout({ children, semRodape = false }: { children: ReactNode; semRodape?: boolean }) {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      <main className="flex-1">{children}</main>
      {!semRodape && <Footer />}
    </div>
  );
}

function Header() {
  const { cidadao, signOut } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAberto, setMenuAberto] = useState(false);
  const [naoLidas, setNaoLidas] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!cidadao) { setNaoLidas(0); return; }
    notificacoesService.contarNaoLidas(cidadao.id).then(setNaoLidas).catch(() => {});
  }, [cidadao]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuAberto(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function sair() {
    await signOut();
    navigate('/login');
  }

  const nomeExib = cidadao?.nome_social || cidadao?.nome || '';
  const iniciais = nomeExib
    ? nomeExib.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()
    : '?';

  const navAtiva = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
      <div className="h-1 bg-gradient-to-r from-institutional via-institutional-light to-institutional" />
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-sm overflow-hidden p-1">
            <img src={branding.logoUrl} alt="" className="h-full w-full object-contain" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900 leading-tight">{branding.nome}</div>
            <div className="text-xs text-slate-500 leading-tight">{branding.slogan || 'Portal do Cidadão'}</div>
          </div>
        </Link>

        {cidadao ? (
          <div className="flex items-center gap-1" ref={menuRef}>
            <NavLink to="/" ativo={navAtiva('/')} icone={<FiHome size={15} />}>Início</NavLink>
            <NavLink to="/minhas-solicitacoes" ativo={navAtiva('/minhas-solicitacoes')} icone={<FiFileText size={15} />}>
              Minhas solicitações
            </NavLink>
            {cidadao?.cnpj && (
              <NavLink to="/fornecedor" ativo={navAtiva('/fornecedor')} icone={<FiTruck size={15} />}>
                Ordens de serviço
              </NavLink>
            )}
            <AcessibilidadeWidget variant="inline" />
            <Link
              to="/notificacoes"
              className="relative ml-1 flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-100"
              aria-label="Notificações"
              title="Notificações"
            >
              <FiBell size={18} />
              {naoLidas > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
                  {naoLidas > 9 ? '9+' : naoLidas}
                </span>
              )}
            </Link>
            <div className="relative">
            <button
              onClick={() => setMenuAberto(!menuAberto)}
              className="ml-2 flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 hover:bg-slate-100 rounded-lg transition"
            >
              <div className="h-8 w-8 rounded-full bg-institutional text-white text-xs font-semibold flex items-center justify-center">
                {iniciais}
              </div>
              <span className="hidden sm:inline text-sm font-medium text-slate-700 max-w-[120px] truncate">
                {nomeExib.split(' ')[0]}
              </span>
              <FiChevronDown size={14} className="hidden sm:block text-slate-400" />
            </button>

            {menuAberto && (
              <div className="absolute right-0 top-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[210px] overflow-hidden z-40">
                <div className="px-4 py-3 border-b border-slate-100">
                  <div className="text-sm font-semibold text-slate-900 truncate">{nomeExib}</div>
                  <div className="text-xs text-slate-500 truncate">{cidadao.email}</div>
                </div>
                <Link
                  to="/perfil"
                  onClick={() => setMenuAberto(false)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <FiUser size={15} /> Meu perfil
                </Link>
                <button
                  onClick={sair}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-700 hover:bg-red-50"
                >
                  <FiLogOut size={15} /> Sair
                </button>
              </div>
            )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/login')}>Entrar</Button>
            <Button onClick={() => navigate('/cadastro')}>Cadastre-se</Button>
          </div>
        )}
      </div>
    </header>
  );
}

function NavLink({ to, ativo, icone, children }: { to: string; ativo: boolean; icone: ReactNode; children: ReactNode }) {
  return (
    <Link
      to={to}
      className={`hidden sm:flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition ${
        ativo ? 'bg-institutional/10 text-institutional font-semibold' : 'text-slate-600 hover:bg-slate-100'
      }`}
    >
      {icone} {children}
    </Link>
  );
}

function Footer() {
  const branding = useBranding();
  const ano = new Date().getFullYear();
  return (
    <footer className="border-t border-slate-200 bg-white py-8 mt-auto">
      <div className="max-w-6xl mx-auto px-4 space-y-4">
        <div className="flex items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-600 flex-wrap">
          <Link to="/servicos" className="hover:text-institutional hover:underline">Carta de Serviços</Link>
          <Link to="/canais" className="hover:text-institutional hover:underline">Canais de Atendimento</Link>
          <Link to="/ouvidoria" className="hover:text-institutional hover:underline">Ouvidoria</Link>
          <Link to="/esic" className="hover:text-institutional hover:underline">e-SIC (LAI)</Link>
          <Link to="/zeladoria" className="hover:text-institutional hover:underline">Zeladoria</Link>
          <Link to="/mapa" className="hover:text-institutional hover:underline">Mapa público</Link>
          <Link to="/conselho" className="hover:text-institutional hover:underline">Conselho de usuários</Link>
          <Link to="/transparencia/ouvidoria" className="hover:text-institutional hover:underline">Transparência</Link>
          <Link to="/verificar" className="inline-flex items-center gap-1 font-medium text-institutional hover:underline">
            <FiShield size={12} /> Verificar autenticidade
          </Link>
        </div>
        <div className="flex flex-col items-center gap-1 text-center text-xs text-slate-400">
          <span>{branding.nome} · Portal do Cidadão · © {ano}</span>
          {/* Acesso ao painel administrativo (servidores). É outro app: em produção
              fica em /admin (mesmo domínio); em dev roda em localhost:5173. Aponta
              direto para a tela de login (/admin/login). */}
          <a href={import.meta.env.DEV ? 'http://localhost:5173/admin/login' : '/admin/login'} className="inline-flex items-center gap-1 text-slate-400 transition hover:text-institutional hover:underline">
            <FiLock size={11} /> Acesso do servidor
          </a>
        </div>
      </div>
    </footer>
  );
}
